import React, { useContext, useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';
import {
  LayoutSingleColumn,
  Loader,
  Page,
  DisabledCtaWithTooltip,
  PrimaryButton,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './CartPage.module.css';
import EmptyCart from './EmptyCart/EmptyCart';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import styles from './CartPage.module.css';
import { getCartErrorMessages } from './CartErrorMessages';
import { removeFromCart, updateCart, updateCartItemsBatch, buildCartGroupKey } from '../../ducks/cart.duck';
import { fetchTransactionLineItems } from './CartPage.duck';
import { useConfiguration } from '../../context/configurationContext';
import { BOOKING_PROCESS_NAME } from '../../transactions/transaction';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

// Lazy-load heavy sub-components — Cart is 994 lines and EstimatedBreakdown is only
// needed when the user clicks "View Summary". Neither should block the initial page paint.
const Cart = loadable(() => import(/* webpackChunkName: "Cart" */ './Cart/Cart'));
const EstimatedBreakdownMaybe = loadable(() => import(/* webpackChunkName: "EstimatedBreakDown" */ './EstimatedBreakDown/EstimatedBreakDown'));
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import moment from 'moment';
import {
  createResourceLocatorString,
  findRouteByRouteName,
} from '../../util/routes';
import { createSlug } from '../../util/urlHelpers';
import { pathByRouteName } from '../../util/routes';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { isWithinNinetyDays } from '../../util/data';
import MobileBottomNavbar from '../../components/MobileBottomNavbar/MobileBottomNavbar';
import { FormattedMessage } from '../../util/reactIntl';
import { applyListingLanguage } from '../../util/listingLanguage';
import { LanguageContext } from '../../context/LanguageContext';
import {
  checkExpiredRentalDates,
  convertLineItemsCurrency,
  buildCartItemsByGroupKey,
  aliasLineItemsForAuthor,
} from './CartHelpers';
import { useActiveAuthorCart } from './hooks/useActiveAuthorCart';
import { useMemo } from 'react';
import { startCheckoutFromItems } from '../CheckoutPage/checkoutFromCart';

const LineItemsModal = props => {
  const lineItemsModelRef = useRef();
  const {
    id,
    isOpen,
    onClose,
    lineItems,
    marketplaceName,
    fetchLineItemsInProgress,
    removeFromCartInProgress,
    setItemsInProgress,
    fetchLineItemsError,
    config,
    routeConfiguration,
    history,
    location,
    intl,
    currentUser,
    callSetInitialValues,
    onInitializeCardPaymentData,
    requestedItems = [],
    handleRequest,
    hasExpiredDates = false,
    currentUserCurrency,
  } = props;

  useEffect(() => {
    lineItemsModelRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const renderEstimatedBreakdown =
    lineItems &&
    lineItems.length &&
    !fetchLineItemsError &&
    !fetchLineItemsInProgress;

  if (fetchLineItemsError) {
    return null;
  }

  const itemsParams = requestedItems?.map(i => ({
    bookingDates: {
      startDate: moment(i.bookingDates.startDate)
        .tz(i.timeZone)
        .toDate(),
      endDate: moment(i.bookingDates.endDate)
        .tz(i.timeZone)
        .toDate(),
    },
    package: i.package,
    title: i.title,
    listing: i.listing,
  }));

  return (
    <div className={css.modalWrapper}>
      {fetchLineItemsInProgress ? (
        <div ref={lineItemsModelRef} id={id} className={css.breakdownWrapper}>
          <Loader className={styles.small} />
        </div>
      ) : renderEstimatedBreakdown && itemsParams?.length ? (
        <div ref={lineItemsModelRef} id={id} className={css.breakdownWrapper}>
          <h4 className={css.totalTitle}>
            <FormattedMessage id="CartPage.orderSummary" />
          </h4>
          <EstimatedBreakdownMaybe
            lineItems={lineItems}
            currency={currentUserCurrency}
            marketplaceName={marketplaceName}
            processName={BOOKING_PROCESS_NAME}
            itemsParams={itemsParams}
            timeZone={requestedItems?.[0]?.timeZone}
            checkoutButton={
              <DisabledCtaWithTooltip
                tooltipContent={
                  hasExpiredDates ? <FormattedMessage id="CartPage.expiredDatesTooltip" /> : null
                }
              >
                <PrimaryButton
                  className={styles.requestButton}
                  type="button"
                  onClick={handleRequest}
                  disabled={hasExpiredDates}
                >
                  {' '}
                  <FormattedMessage id="OrderBreakdown.checkoutNow" />
                </PrimaryButton>
              </DisabledCtaWithTooltip>
            }
            showBookingSupport={true}
            showSecurely={true}
          />
        </div>
      ) : null}
    </div>
  );
};

export default function CartPage(props) {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const config = useConfiguration();
  const scrollingDisabled = isScrollingDisabled(useSelector(state => state));
  const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false);
  const { locale } = useContext(LanguageContext);

  const dispatch = useDispatch();
  const {
    fetchLineItemsInProgress,
    lineItems,
    fetchLineItemsError,
  } = useSelector(state => state.CartPage);

  const cart = useSelector(state => state.cart);
  const { currentUser, currentUserCurrency } = useSelector(state => state.user);
  const { exchangeRates, fetchRatesInProgress } = useSelector(
    state => state.exchangeRate
  );

  const {
    items: rawItems = {},
    setItemsInProgress,
    addToCartInProgress,
    setItemsError,
    notification,
    updateCartInProgress,
    updateCartError,
    removeFromCartInProgress,
    removeFromCartError,
    removableItemId,
  } = cart;

  const items = rawItems && Object.entries(rawItems).reduce((acc, [authorId, bookings]) => {
    acc[authorId] = bookings.map(booking => ({
      ...booking,
      listing: applyListingLanguage(booking?.listing, locale),
    }));
    return acc;
  }, {});

  // ONE cart per (store, date range), most recent first - see buildCartItemsByGroupKey.
  // From here down every "author" key (activeAuthorId, lineItems[...], the hook, the
  // cards) is really a cart-group key; the real author id is read off the items.
  // `items` is null on a hard reload until the cart has been fetched (state.cart.items
  // starts as null) - the page shows its loader meanwhile, but this memo still runs.
  const cartItemsByGroup = useMemo(() => buildCartItemsByGroupKey(items || {}), [items]);

  // Custom hook to manage the active cart and its line-items fetching
  const {
    activeAuthorId,
    activeItems,
    updatingAuthorId,
    setUpdatingAuthorId,
    switchToAuthor,
  } = useActiveAuthorCart({
    items: cartItemsByGroup,
    updateCartInProgress,
    removeFromCartInProgress,
    addToCartInProgress,
  });

  // Convert line items to user's currency
  const convertedLineItems = useMemo(() => {
    return convertLineItemsCurrency(lineItems, exchangeRates, currentUserCurrency);
  }, [lineItems, exchangeRates, currentUserCurrency]);

  // Only show empty cart when items are genuinely absent — don't gate on exchange rates
  // so cart content renders immediately while rates load in the background.
  const showEmptyCart = !setItemsInProgress && !Object.keys(items || {}).length;

  const renderCart = !setItemsInProgress && !addToCartInProgress && !showEmptyCart;
  const errorMessages = getCartErrorMessages(
    setItemsError,
    updateCartError,
    removeFromCartError
  );

  // `cartKey` is the card being checked out - defaults to the active one (desktop's
  // summary modal), but mobile's per-card Checkout button passes its own.
  // Inline date edit on a cart card (client feedback 2026-09-17). Every item in the
  // card is re-dated in one atomic write (updateCartItemsBatch - same as the side
  // carts), after which those items live under a NEW (store, dates) key - possibly one
  // that already exists, in which case the two cards merge. The new key is named up
  // front with the grouping's own rule so useActiveAuthorCart can follow the items
  // there once the write lands (it treats the "updating" key as the successor of an
  // active key that disappears) and re-price them under it.
  const onUpdateCartDates = (cartKey, authorId, itemIds, bookingDates) => {
    setUpdatingAuthorId(buildCartGroupKey(authorId, bookingDates.startDate, bookingDates.endDate));
    return dispatch(updateCartItemsBatch(itemIds, bookingDates));
  };

  // "← Back" under the page heading: in-app history when there is any (the store or
  // search the shopper came from, query string intact), else the search page - same
  // rule as ListingTopbar.js. A direct/shared /cart link has nothing to go back to.
  const handleBack = () => {
    const hasHistory = typeof window !== 'undefined' && window.history && window.history.length > 1;
    if (hasHistory) {
      history.goBack();
    } else {
      history.push(pathByRouteName('SearchPage', routeConfiguration, {}));
    }
  };
  const openCartCount = Object.keys(cartItemsByGroup).length;

  const handleRequest = async (items, cartKey = activeAuthorId) => {
    // Strip deleted listings before proceeding to checkout
    const validItems = (items || []).filter(item => item.listing?.id);
    await startCheckoutFromItems({
      items: validItems,
      activeAuthorId: cartKey,
      // Checkout reads this cart's line items under the group key AND, per item,
      // under the real author id - serve both.
      lineItems: aliasLineItemsForAuthor(lineItems, cartKey, validItems[0]?.authorId),
      fetchLineItemsInProgress,
      setItemsInProgress,
      removeFromCartInProgress,
      currentUser,
      routeConfiguration,
      dispatch,
      history,
    });
  };

  return (
    <Page title={intl.formatMessage({ id: 'CartPage.title' })} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        className={css.pageRoot}
        mobileBottomNavbar={<MobileBottomNavbar />}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.cartWrapper}>
          {renderCart ? (
            <header className={css.pageHeader}>
              <h1 className={css.pageHeading}>
                <FormattedMessage
                  id="CartPage.openCartsHeading"
                  values={{ count: openCartCount }}
                />
              </h1>
              <button type="button" className={css.backButton} onClick={handleBack}>
                <i className="ri-arrow-left-line" aria-hidden="true" />
                <FormattedMessage id="CartPage.back" />
              </button>
            </header>
          ) : null}
          <div>
            {errorMessages.setCartItemsError}
            {errorMessages.updateItemError}
            {errorMessages.removeItemError}
            {errorMessages.lineItemsError}
          </div>
          {setItemsInProgress || addToCartInProgress ? (
            <Loader />
          ) : showEmptyCart ? (
            <EmptyCart />
          ) : renderCart ? (
            <Cart
              items={cartItemsByGroup}
              history={history}
              intl={intl}
              currentUserCurrency={currentUserCurrency}
              exchangeRates={exchangeRates}
              exchangeRatesReady={!!exchangeRates?.length}
              activeAuthorId={activeAuthorId}
              updatingAuthorId={updatingAuthorId}
              switchToAuthor={switchToAuthor}
              updateCart={params => {
                // params.cartKey is the card's group key (what the hook tracks);
                // authorId stays the real author for the write itself.
                setUpdatingAuthorId(params.cartKey || params.authorId);
                const { cartKey: _omit, ...writeParams } = params;
                dispatch(updateCart(writeParams));
              }}
              removeFromCart={itemId => dispatch(removeFromCart(itemId))}
              removeFromCartInProgress={removeFromCartInProgress}
              removableItemId={removableItemId}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              handleRequest={handleRequest}
              updateCartInProgress={updateCartInProgress}
              onUpdateCartDates={onUpdateCartDates}
              lineItemModal={
                <LineItemsModal
                  id="line-item-modal"
                  lineItems={convertedLineItems?.[activeAuthorId]}
                  isOpen={isLineItemModalOpen}
                  onClose={() => setIsLineItemModalOpen(false)}
                  marketplaceName={config.marketplaceName}
                  fetchLineItemsInProgress={fetchLineItemsInProgress[activeAuthorId]}
                  removeFromCartInProgress={removeFromCartInProgress}
                  setItemsInProgress={setItemsInProgress}
                  fetchLineItemsError={fetchLineItemsError[activeAuthorId]}
                  config={config}
                  routeConfiguration={routeConfiguration}
                  history={history}
                  location={location}
                  intl={intl}
                  currentUser={currentUser}
                  currentUserCurrency={currentUserCurrency}
                  callSetInitialValues={(
                    setInitialValues,
                    values,
                    saveToSessionStorage
                  ) => {
                    dispatch(setInitialValues(values, saveToSessionStorage));
                  }}
                  onInitializeCardPaymentData={() =>
                    dispatch(initializeCardPaymentData())
                  }
                  requestedItems={activeItems}
                  handleRequest={() => handleRequest(activeItems, activeAuthorId)}
                  hasExpiredDates={checkExpiredRentalDates(activeItems).hasExpiredDates}
                />
              }
              lineItems={convertedLineItems}
            />
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
}
